import { useChat } from "@ai-sdk/react";
import { HUB_BASE_URL } from "@constants/env";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import type { OriginalItemTypes, SubskillAssessment } from "@memorang/types";
import { DefaultChatTransport } from "ai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SuperJSON from "superjson";

import { isPteAssessorSpeakingItem } from "../constants/pte/assessor";
import { agentStreamFetch } from "../helpers/agent-stream";
import { buildAdditionalContextBody } from "../helpers/extract-image-files";
import { extractItemContent } from "../helpers/extract-item-content";
import {
	buildAudioFileName,
	rawAnswerOf,
	toAudioResponse,
	toTextResponse,
} from "../helpers/pte-user-response";
import { uploadRecording } from "../helpers/recording";
import { buildSessionScopeBody } from "../helpers/session-scope-body";
import type {
	PteAssessorPhase,
	PteAssessorUIDataTypes,
	PteAssessorUIMessage,
	PteCriterionSkeletonRow,
	PteWorkflowUserResponse,
} from "../types/pte-assessor";
import useAIFeedbackStore from "./useAIFeedbackStore";
import useCurrentSession from "./useCurrentSession";
import useSessionStore from "./useSessionStore";

const transport = new DefaultChatTransport<PteAssessorUIMessage>({
	api: `${HUB_BASE_URL}${HUB_ENDPOINTS.ASSESSOR_AGENT}`,
	fetch: agentStreamFetch,
});

type ScoredCriterion = {
	result: number;
	displayResult?: number;
	normalizedResult?: number;
	multiplier?: number;
	summary: string;
};

type ReducedMessageState = {
	skeletonRows: PteCriterionSkeletonRow[] | null;
	criterionByKey: Map<string, ScoredCriterion>;
	overallResult?: number;
	overallDisplayResult?: number;
	overallNormalizedResult?: number;
	feedbackText: string;
	transcription?: string;
};

const criterionKeyOf = (row: { id?: string; name: string }): string =>
	row.id ?? row.name;

const applySkeleton = (
	data: PteAssessorUIDataTypes["criterion-skeleton"],
	acc: ReducedMessageState,
): void => {
	acc.skeletonRows = data.criteria;
	for (const row of data.criteria) {
		if (typeof row.result === "number") {
			acc.criterionByKey.set(criterionKeyOf(row), {
				result: row.result,
				summary: row.summary ?? "",
			});
		}
	}
};

const applyCriterionStream = (
	data: PteAssessorUIDataTypes["criterion-stream"],
	acc: ReducedMessageState,
): void => {
	if (data.name && typeof data.result === "number") {
		acc.criterionByKey.set(data.id ?? data.name, {
			result: data.result,
			displayResult: data.displayResult,
			normalizedResult: data.normalizedResult,
			multiplier: data.multiplier,
			summary: data.summary ?? "",
		});
	}
};

const reduceMessageParts = (
	parts: PteAssessorUIMessage["parts"],
): ReducedMessageState => {
	const acc: ReducedMessageState = {
		skeletonRows: null,
		criterionByKey: new Map(),
		feedbackText: "",
	};

	for (const part of parts) {
		switch (part.type) {
			case "data-criterion-skeleton":
				applySkeleton(part.data, acc);
				break;
			case "data-criterion-stream":
				applyCriterionStream(part.data, acc);
				break;
			case "data-assessor-overall-evaluation-stream":
				if (typeof part.data.result === "number") {
					acc.overallResult = part.data.result;
					acc.overallDisplayResult = part.data.displayResult;
					acc.overallNormalizedResult = part.data.normalizedResult;
				}
				break;
			case "data-feedback-stream":
				if (typeof part.data.feedback === "string") {
					acc.feedbackText = part.data.feedback;
				}
				break;
			case "data-transcript-stream":
				if (typeof part.data.transcription === "string") {
					acc.transcription = part.data.transcription;
				}
				break;
			default:
				break;
		}
	}

	return acc;
};

type StoreSync = {
	updateSubskills: (itemId: number, data: SubskillAssessment[]) => void;
	updateScore: (itemId: number, data: number | undefined) => void;
	updateDisplayScore: (itemId: number, data: number | undefined) => void;
	updateNormalizedPoints: (itemId: number, data: number | undefined) => void;
	updateExplanation: (itemId: number, data: string) => void;
	updateTranscription: (itemId: number, data: string) => void;
};

const sameAssessments = (
	a: SubskillAssessment[],
	b?: readonly SubskillAssessment[],
): boolean =>
	Array.isArray(b) &&
	a.length === b.length &&
	a.every(
		(row, i) =>
			row.key === b[i]?.key &&
			row.name === b[i]?.name &&
			row.score === b[i]?.score &&
			row.displayScore === b[i]?.displayScore &&
			row.maxScore === b[i]?.maxScore &&
			row.normalizedScore === b[i]?.normalizedScore &&
			row.multiplier === b[i]?.multiplier,
	);

const collectAssessments = (
	state: ReducedMessageState,
): SubskillAssessment[] => {
	const assessments: SubskillAssessment[] = [];
	for (const row of state.skeletonRows ?? []) {
		const scored = state.criterionByKey.get(criterionKeyOf(row));
		if (scored) {
			assessments.push({
				key: row.id,
				name: row.name,
				score: scored.result,
				displayScore: scored.displayResult,
				maxScore: row.maxPoints,
				...(scored.normalizedResult !== undefined
					? { normalizedScore: scored.normalizedResult }
					: {}),
				...(scored.multiplier !== undefined
					? { multiplier: scored.multiplier }
					: {}),
			});
		}
	}
	return assessments;
};

const syncMessageToStore = (
	targetId: number,
	state: ReducedMessageState,
	store: StoreSync,
): void => {
	const current = useAIFeedbackStore.getState().aiResponse?.[targetId];

	if (state.transcription && state.transcription !== current?.transcription) {
		store.updateTranscription(targetId, state.transcription);
	}
	if (state.skeletonRows && state.criterionByKey.size > 0) {
		const assessments = collectAssessments(state);
		if (
			assessments.length > 0 &&
			!sameAssessments(assessments, current?.subskillAssessments)
		) {
			store.updateSubskills(targetId, assessments);
		}
	}
	if (
		typeof state.overallResult === "number" &&
		state.overallResult !== current?.score
	) {
		store.updateScore(targetId, state.overallResult);
	}
	if (
		typeof state.overallDisplayResult === "number" &&
		state.overallDisplayResult !== current?.displayScore
	) {
		store.updateDisplayScore(targetId, state.overallDisplayResult);
	}
	if (
		typeof state.overallNormalizedResult === "number" &&
		state.overallNormalizedResult !== current?.normalizedPoints
	) {
		store.updateNormalizedPoints(targetId, state.overallNormalizedResult);
	}
	if (state.feedbackText && state.feedbackText !== current?.explanation) {
		store.updateExplanation(targetId, state.feedbackText);
	}
};

type ResolveUserResponseDeps = {
	itemId: number;
	itemTypeId: OriginalItemTypes;
	userId: string;
	sessionId: number;
};

const resolveUserResponse = async (
	deps: ResolveUserResponseDeps,
): Promise<
	| { ok: true; userResponse: PteWorkflowUserResponse }
	| { ok: false; phase: PteAssessorPhase }
> => {
	const { itemId, itemTypeId, userId, sessionId } = deps;
	const isSpeaking = isPteAssessorSpeakingItem(itemTypeId);
	const existing = useAIFeedbackStore.getState().userResponse?.[itemId];

	if (!isSpeaking) {
		const text = toTextResponse(existing);
		return text
			? { ok: true, userResponse: text }
			: { ok: false, phase: "idle" };
	}

	const blobOrUri = existing as Blob | string | undefined;
	if (!blobOrUri) {
		return { ok: false, phase: "idle" };
	}
	const fileName = buildAudioFileName(userId, sessionId, itemId);
	const url = await uploadRecording(blobOrUri, fileName);
	if (!url) {
		return { ok: false, phase: "error" };
	}
	return { ok: true, userResponse: toAudioResponse(url) };
};

export const usePteAssessorAgent = ({
	onComplete,
}: { onComplete?: () => void } = {}) => {
	const { userId } = useAuthContext();
	const {
		currentExamContext: { projectId, examId },
	} = useExamContext();
	const { currentItem } = useCurrentSession();
	const sessionId = useSessionStore((s) => s.id);
	const { i18n } = useTranslation();

	const setStreaming = useAIFeedbackStore((s) => s.setStreaming);
	const updateScore = useAIFeedbackStore((s) => s.updateScore);
	const updateDisplayScore = useAIFeedbackStore((s) => s.updateDisplayScore);
	const updateNormalizedPoints = useAIFeedbackStore(
		(s) => s.updateNormalizedPoints,
	);
	const updateExplanation = useAIFeedbackStore((s) => s.updateExplanation);
	const updateSubskills = useAIFeedbackStore(
		(s) => s.updateSubskillAssessments,
	);
	const updateTranscription = useAIFeedbackStore((s) => s.updateTranscription);
	const updateAnswer = useAIFeedbackStore((s) => s.updateAnswer);

	const currentItemRef = useRef(currentItem);
	currentItemRef.current = currentItem;
	const targetItemIdRef = useRef<number | undefined>(undefined);
	const [phase, setPhase] = useState<PteAssessorPhase>("idle");

	const onCompleteRef = useRef(onComplete);
	onCompleteRef.current = onComplete;
	const completionFiredRef = useRef(false);

	const { messages, sendMessage, setMessages, stop } =
		useChat<PteAssessorUIMessage>({
			transport,
			onFinish: () => {
				setPhase("ready");
				setStreaming(false);
			},
			onError: (err) => {
				setPhase("error");
				setStreaming(false);
				captureErrorToSentry(new Error("PTE assessor stream failed"), {
					error: serializeErrorForSentry(err),
				});
			},
		});

	useEffect(() => {
		const targetId = targetItemIdRef.current;
		if (!targetId || messages.length === 0) {
			return;
		}
		const last = messages[messages.length - 1];
		if (last?.role !== "assistant") {
			return;
		}
		const reduced = reduceMessageParts(last.parts ?? []);
		syncMessageToStore(targetId, reduced, {
			updateScore,
			updateDisplayScore,
			updateNormalizedPoints,
			updateExplanation,
			updateSubskills,
			updateTranscription,
		});
	}, [
		messages,
		updateScore,
		updateDisplayScore,
		updateNormalizedPoints,
		updateExplanation,
		updateSubskills,
		updateTranscription,
	]);

	useEffect(() => {
		if (phase === "ready" && !completionFiredRef.current) {
			completionFiredRef.current = true;
			onCompleteRef.current?.();
		}
	}, [phase]);

	const currentItemId = currentItem?.id;
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset assessor state on item change
	useEffect(() => {
		stop();
		setPhase("idle");
		setStreaming(false);
		setMessages([]);
		targetItemIdRef.current = undefined;
	}, [currentItemId, setMessages, setStreaming, stop]);

	const generate = useCallback(async () => {
		if (useAIFeedbackStore.getState().streaming) {
			return;
		}

		const item = currentItemRef.current;
		const itemId = item?.id;
		if (!(itemId && userId)) {
			return;
		}

		const itemTypeId = item.metadata?.type as OriginalItemTypes | undefined;
		if (!itemTypeId) {
			return;
		}

		targetItemIdRef.current = itemId;
		completionFiredRef.current = false;
		setMessages([]);
		updateExplanation(itemId, "");
		updateSubskills(itemId, []);
		updateScore(itemId, undefined);
		updateDisplayScore(itemId, undefined);
		updateNormalizedPoints(itemId, undefined);
		updateTranscription(itemId, "");
		setStreaming(true);
		setPhase(
			isPteAssessorSpeakingItem(itemTypeId) ? "uploading" : "submitting",
		);

		const resolved = await resolveUserResponse({
			itemId,
			itemTypeId,
			userId,
			sessionId,
		});
		if (targetItemIdRef.current !== itemId) {
			return;
		}
		if (!resolved.ok) {
			setStreaming(false);
			setPhase(resolved.phase);
			return;
		}

		updateAnswer(itemId, rawAnswerOf(resolved.userResponse));

		setPhase("submitting");
		const additionalContextBody = buildAdditionalContextBody(item);
		sendMessage(
			{ text: "Evaluate" },
			{
				body: {
					inputs: SuperJSON.serialize({
						itemTypes: [itemTypeId],
						item: { id: String(itemId), content: extractItemContent(item) },
						userResponse: resolved.userResponse,
						runMode: "full",
						outputLanguage: i18n.language,
					}),
					...additionalContextBody,
					userId,
					projectId,
					...buildSessionScopeBody({ sessionId, examId }),
				},
			},
		);
		setPhase("phase-1-streaming");
	}, [
		userId,
		projectId,
		sessionId,
		examId,
		i18n.language,
		sendMessage,
		setMessages,
		setStreaming,
		updateExplanation,
		updateSubskills,
		updateScore,
		updateDisplayScore,
		updateNormalizedPoints,
		updateTranscription,
		updateAnswer,
	]);

	return { generate, phase };
};
